import React, { useState, useEffect, useRef } from 'react';
import './App.css';
import TopBrokers from './components/TopBrokers';

// API base URLs (override via frontend/.env)
const SERVER_API_BASE = process.env.REACT_APP_SERVER_API_URL || 'http://localhost:3002';
const ALGO_API_BASE = process.env.REACT_APP_ALGO_API_URL || process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';

function AlgoEngineSection({ scriptName, activeTab, displayedTables, activeKey, startDate, endDate }) {
  // Helper to get current Nepal date (UTC+5:45)
  const getNepalDate = (dateObj = new Date()) => {
    // Add 5 hours and 45 minutes offset to UTC
    const offsetInMs = (5 * 60 + 45) * 60 * 1000;
    const nepalDate = new Date(dateObj.getTime() + offsetInMs);
    return nepalDate.toISOString().split('T')[0];
  };

  const [signal, setSignal] = useState(null);
  const [backtest, setBacktest] = useState([]);
  const [performance, setPerformance] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  const initialToDate = endDate || getNepalDate();
  const initialFromDate = startDate || getNepalDate(new Date(new Date().setFullYear(new Date().getFullYear() - 1)));

  const [backtestParams, setBacktestParams] = useState({
    fromDate: initialFromDate,
    toDate: initialToDate,
    initialCapital: 100000,
    strategyName: 'AccumulationStrategy',
    minPctAccumulation: 0
  });

  // Sync backtest dates with header range buttons
  useEffect(() => {
    if (startDate || endDate) {
      setBacktestParams(prev => ({
        ...prev,
        fromDate: startDate || prev.fromDate,
        toDate: endDate || prev.toDate
      }));
    }
  }, [startDate, endDate]);

  const hasScript = !!(scriptName && scriptName.trim());

  const fetchAll = async () => {
    if (!hasScript) {
      setError('Please enter a Script Name (e.g. NABIL) in the header first.');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      // 1) Latest signal - use the EndDate from the header if available
      const signalUrl = `${ALGO_API_BASE}/api/signal/${encodeURIComponent(scriptName.trim())}?min_pct=${backtestParams.minPctAccumulation}${endDate ? `&end_date=${endDate}` : ''}`;
      console.log('Fetching signal from:', signalUrl);
      const sigResp = await fetch(signalUrl);
      if (!sigResp.ok) {
        const errText = await sigResp.text();
        console.error('Signal API error:', errText);
        throw new Error(`Signal error: ${sigResp.status} ${sigResp.statusText}`);
      }
      const sigJson = await sigResp.json();
      console.log('Signal data received:', sigJson);
      setSignal(sigJson);

      // 2) Backtest history
      const btUrl = `${ALGO_API_BASE}/api/backtest/${encodeURIComponent(scriptName.trim())}`;
      console.log('Fetching backtest from:', btUrl);
      const btResp = await fetch(btUrl);
      if (!btResp.ok) {
        const errText = await btResp.text();
        console.error('Backtest API error:', errText);
        throw new Error(`Backtest error: ${btResp.status} ${btResp.statusText}`);
      }
      const btJson = await btResp.json();
      // Ensure we map snake_case from SP to camelCase for the UI if necessary
      const mappedBacktest = (btJson || []).map(t => ({
        entryDate: t.EntryDate || t.entryDate,
        entryPrice: t.EntryPrice || t.entryPrice,
        exitDate: t.ExitDate || t.exitDate,
        exitPrice: t.ExitPrice || t.exitPrice,
        returnPct: t.ReturnPct || t.returnPct,
        holdingDays: t.HoldingDays || t.holdingDays,
        winLoss: t.WinLoss || t.winLoss
      }));
      setBacktest(mappedBacktest);

      // 3) Performance summary
      const perfUrl = `${ALGO_API_BASE}/api/performance-summary?script=${encodeURIComponent(scriptName.trim())}`;
      console.log('Fetching performance from:', perfUrl);
      const perfResp = await fetch(perfUrl);
      if (!perfResp.ok) {
        if (perfResp.status === 404) {
          console.warn('No performance data found for this script.');
          setPerformance(null);
        } else {
          const errText = await perfResp.text();
          console.error('Performance API error:', errText);
          throw new Error(`Performance error: ${perfResp.status} ${perfResp.statusText}`);
        }
      } else {
        const perfJson = await perfResp.json();
        // Map PascalCase from SP result to camelCase for UI
        if (perfJson) {
          setPerformance({
            winRate: (perfJson.WinRate || 0) / 100, // SP returns percentage (62.22), UI expects decimal (0.62)
            avgReturn: (perfJson.AvgReturnPct || 0) / 100,
            maxReturn: perfJson.MaxReturn,
            minReturn: perfJson.MinReturn,
            totalTrades: perfJson.TotalTrades,
            sharpeRatio: perfJson.SharpeRatio,
            wins: perfJson.Wins,
            losses: perfJson.Losses
          });
        }
      }
    } catch (err) {
      console.error('AlgoEngineSection error:', err);
      setError(err.message || 'Failed to load algo engine data');
    } finally {
      setLoading(false);
    }
  };

  const runNewBacktest = async () => {
    if (!hasScript) return;
    setLoading(true);
    setError(null);
    try {
      const resp = await fetch(`${ALGO_API_BASE}/api/run-backtest`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          scriptName: scriptName.trim(),
          fromDate: backtestParams.fromDate,
          toDate: backtestParams.toDate,
          initialCapital: backtestParams.initialCapital,
          strategyName: backtestParams.strategyName,
        })
      });
      if (!resp.ok) {
        const errJson = await resp.json().catch(() => ({ detail: 'Unknown server error' }));
        console.error('Backtest API error details:', errJson);
        throw new Error(errJson.detail || `Backtest failed: ${resp.status} ${resp.statusText}`);
      }
      
      const summary = await resp.json();
      if (summary) {
        // Display summary metrics to the user (optional enhancement)
        console.log('Backtest summary:', summary);
      }

      // Re-fetch all to see the new backtest results in the table and performance section
      await fetchAll();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab === displayedTables.length - 1 && activeKey === 'algoEngine') {
      fetchAll();
    }
  }, [activeTab, activeKey, displayedTables.length]);

  useEffect(() => {
    setSignal(null);
    setBacktest([]);
    setPerformance(null);
    setError(null);
  }, [scriptName]);

  const [showCreativity, setShowCreativity] = useState(false);

  return (
    <div className="algo-engine-container">
      <h2 className="algo-engine-title">Algorithmic Trading Engine</h2>
      <p className="algo-engine-subtitle">
        Automated trading signals, backtesting, risk management, and performance analytics powered by NEPSE broker activity.
      </p>

      {/* Tab Content */}
      <div style={{ marginTop: 16 }}>
        <button
          type="button"
          className="algo-primary-btn"
          onClick={fetchAll}
          disabled={loading}
        >
          {loading ? 'Loading...' : 'Refresh Algo Insights'}
        </button>
        <button
          type="button"
          className="algo-primary-btn"
          onClick={() => setShowCreativity(s => !s)}
          style={{ marginLeft: 8, background: '#10b981', color: '#fff' }}
        >
          Creativity Reminder
        </button>
        <span style={{ marginLeft: 12, fontSize: '0.9rem', color: '#ccc' }}>
          Script: <strong>{hasScript ? scriptName.toUpperCase() : '— (enter above)'}</strong>
        </span>
      </div>

      {error && <div className="error-message" style={{ margin: '16px 0' }}>{error}</div>}

      {showCreativity && (
        <CreativityEmbed scriptName={scriptName} />
      )}

      <div className="algo-backtest-config algo-card" style={{ marginBottom: 16 }}>
        <h3 style={{ margin: '0 0 12px 0', fontSize: '1.1rem' }}>Backtest Configuration</h3>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, alignItems: 'flex-end' }}>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: '0.85rem' }}>
            From Date
            <input 
              type="date" 
              className="header-script-input"
              value={backtestParams.fromDate}
              onChange={e => setBacktestParams({...backtestParams, fromDate: e.target.value})}
              style={{ width: 140 }}
            />
          </label>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: '0.85rem' }}>
            To Date
            <input 
              type="date" 
              className="header-script-input"
              value={backtestParams.toDate}
              onChange={e => setBacktestParams({...backtestParams, toDate: e.target.value})}
              style={{ width: 140 }}
            />
          </label>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: '0.85rem' }}>
            Initial Capital
            <input 
              type="number" 
              className="header-script-input"
              value={backtestParams.initialCapital}
              onChange={e => setBacktestParams({...backtestParams, initialCapital: parseInt(e.target.value) || 0})}
              style={{ width: 120 }}
            />
          </label>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: '0.85rem' }}>
            Min Acc %
            <input 
              type="number" 
              className="header-script-input"
              value={backtestParams.minPctAccumulation}
              onChange={e => setBacktestParams({...backtestParams, minPctAccumulation: parseFloat(e.target.value) || 0})}
              style={{ width: 80 }}
            />
          </label>
          <button 
            type="button"
            className="algo-primary-btn" 
            onClick={runNewBacktest} 
            disabled={loading || !hasScript}
            style={{ background: 'var(--theme-accent, #3949ab)', color: 'white' }}
          >
            {loading ? 'Running...' : 'Run New Backtest'}
          </button>
        </div>
      </div>

      <div className="algo-engine-grid">
        <section className="algo-card">
          <h3>Trading Signals</h3>
          {signal ? (
            <div>
              <div className="algo-signal-row">
                <span className={`algo-signal-badge algo-signal-${signal.signal.toLowerCase()}`}>
                  {signal.signal}
                </span>
                <span className="algo-signal-meta">
                  Strength: <strong>{signal.strengthScore.toFixed(2)}</strong> ({signal.confidenceLevel})
                </span>
              </div>
              <div className="algo-signal-kpis">
                <div>
                  <div className="kpi-label">Entry</div>
                  <div className="kpi-value">{signal.entryPrice.toFixed(2)}</div>
                </div>
                <div>
                  <div className="kpi-label">Stop Loss</div>
                  <div className="kpi-value kpi-loss">{signal.stopLoss.toFixed(2)}</div>
                </div>
                <div>
                  <div className="kpi-label">Target</div>
                  <div className="kpi-value kpi-target">{signal.targetPrice.toFixed(2)}</div>
                </div>
                <div>
                  <div className="kpi-label">LTP</div>
                  <div className="kpi-value">{signal.ltp != null ? Number(signal.ltp).toFixed(2) : '—'}</div>
                </div>
                {signal.positionSize != null && (
                  <div>
                    <div className="kpi-label">Pos. Size (shares)</div>
                    <div className="kpi-value">{Math.floor(signal.positionSize).toLocaleString()}</div>
                  </div>
                )}
              </div>
              <div className="algo-signal-footer">
                Generated: {new Date(signal.generatedDate).toLocaleString()} ({signal.timeFrame})
              </div>
            </div>
          ) : (
            <p className="algo-card-hint">
              Click <strong>Refresh Algo Insights</strong> to fetch the latest BUY / SELL / HOLD signal for the current script.
            </p>
          )}
        </section>

        <section className="algo-card">
          <h3>Backtesting Engine</h3>
          {backtest && backtest.length > 0 ? (
            <div className="algo-backtest-table-wrapper">
              <table className="algo-backtest-table">
                <thead>
                  <tr>
                    <th>Entry</th>
                    <th>Exit</th>
                    <th>Ret %</th>
                    <th>Days</th>
                    <th>W/L</th>
                  </tr>
                </thead>
                <tbody>
                  {backtest.slice(-10).map((t, idx) => (
                    <tr key={idx}>
                      <td>{new Date(t.entry_date || t.entryDate).toISOString().slice(0, 10)}</td>
                      <td>{new Date(t.exit_date || t.exitDate).toISOString().slice(0, 10)}</td>
                      <td className={t.return_pct > 0 || t.returnPct > 0 ? 'pos' : 'neg'}>
                        {((t.return_pct ?? t.returnPct) * 100).toFixed(2)}
                      </td>
                      <td>{t.holding_days ?? t.holdingDays}</td>
                      <td>{t.win_loss ?? t.winLoss}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="algo-card-hint">
                Showing last 10 trades from <code>/api/backtest/{'{script}'}</code>.
              </div>
            </div>
          ) : (
            <p className="algo-card-hint">
              No backtest trades found yet. Once you run your Python backtest jobs, they will appear here.
            </p>
          )}
        </section>

        <section className="algo-card">
          <h3>Performance &amp; Risk</h3>
          {performance ? (
            <div className="algo-performance-grid">
              <div>
                <div className="kpi-label">Win Rate</div>
                <div className="kpi-value">
                  {(performance.winRate * 100).toFixed(1)}%
                </div>
              </div>
              <div>
                <div className="kpi-label">Avg Return / Trade</div>
                <div className="kpi-value">
                  {(performance.avgReturn * 100).toFixed(2)}%
                </div>
              </div>
              <div>
                <div className="kpi-label">Max Drawdown</div>
                <div className="kpi-value kpi-loss">
                  {(performance.maxDrawdown * 100).toFixed(2)}%
                </div>
              </div>
              <div>
                <div className="kpi-label">Profit Factor</div>
                <div className="kpi-value">
                  {performance.profitFactor === Infinity
                    ? '∞'
                    : performance.profitFactor.toFixed(2)}
                </div>
              </div>
              <div>
                <div className="kpi-label">Sharpe Ratio</div>
                <div className="kpi-value">
                  {performance.sharpeRatio.toFixed(2)}
                </div>
              </div>
            </div>
          ) : (
            <p className="algo-card-hint">
              When backtest results are available, summary metrics (win rate, max drawdown,
              profit factor, Sharpe) will be displayed here.
            </p>
          )}
        </section>
      </div>
    </div>
  );
}

function CreativityEmbed({ scriptName }) {
  const iframeRef = React.useRef(null);
  React.useEffect(() => {
    const handler = (e) => {
      if (e?.data?.type === 'creativity-height' && iframeRef.current) {
        const h = Math.min(1600, Math.max(360, parseInt(e.data.height, 10) || 600));
        iframeRef.current.style.height = `${h}px`;
      }
    };
    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, []);
  return (
    <div className="algo-card" style={{ marginBottom: 16, overflow: 'visible' }}>
      <iframe
        ref={iframeRef}
        title="Creativity Reminder"
        src={`${ALGO_API_BASE}/creativity?script=${encodeURIComponent(scriptName || '')}`}
        style={{ width: '100%', height: 600, border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)' }}
      />
    </div>
  );
}

const TABLE_LABELS = [
  'Brk Analysis',   // Broker Analysis
  'Mtk Summary',    // Market SummaryI
  'Top ACC',        // Top Accumulators
  'ACC Total',      // Top Accumulators Total
  'Top Diss',       // Top Distributors
  'Diss Total',     // Top Distributors Total
  'Acc/Dis',        // Accumulation/Distribution
  'FA',             // Final Analysis
  'Summary',        // Summary Tables
  'Algo Engine'     // Algorithmic Trading Engine
];

// Map API result keys to friendly tab labels. This keeps the UI in sync with backend result set keys
const KEY_LABEL_MAP = {
  brokerAnalysis: 'Brk Analysis',
  marketSummary: 'Mtk Summary',
  topAccumulators: 'Top ACC',
  topAccumulatorsTotal: 'ACC Total',
  topDistributors: 'Top Diss',
  topDistributorsTotal: 'Diss Total',
  accumulationDistribution: 'Acc/Dis',
  finalAnalysis: 'FA',
  summary: 'Summary',
  algoEngine: 'Algo Engine'
};

const RECORDS_PER_PAGE_OPTIONS = [10, 25, 50, Infinity];

// Broker IDs to highlight in Top ACC tab
// const HIGHLIGHT_BROKER_IDS = new Set([56, 58, 34, 45, 49, 39]);//Purano list
const HIGHLIGHT_BROKER_IDS = new Set([56, 58, 34, 45, 49, 39,57,44,38,17,56]);

const THEMES = [
  { value: '', label: '--Select Theme--' },
  { value: 'high-contrast', label: 'High-Contrast Trading Theme' },
  { value: 'minimalist-dark', label: 'Minimalist Dark Theme' },
  { value: 'professional-light', label: 'Professional Light Theme' },
  { value: 'material-ui', label: 'Material-UI Data Grid Theme' }
];

const FA_TAB_INDEX = TABLE_LABELS.indexOf('FA'); // 'FA' is the target default tab

// Add a mapping from script code to full name
const SCRIPT_NAME_MAP = {
  UMRH: 'UMRH - United Modi Hydropower Ltd.',
  NABIL: 'NABIL - Nabil Bank Limited',
  NLIC: 'NLIC - Nepal Life Insurance Co.',
  // Add more mappings as needed
};

// Add a themed loader component
function ThemedLoader({ theme }) {
  return (
    <div className="page-loader-container">
      <div className={`page-loader spinner-${theme || 'default'}`}></div>
    </div>
  );
}

// BrokerAnalysisTable - Redesigned broker analysis with modern styling
function BrokerAnalysisTable({ tableData, highlightToggles }) {
  const [sortConfig, setSortConfig] = useState({ key: 'netQuantity', direction: 'desc' });
  const [currentPage, setCurrentPage] = useState(1);
  const recordsPerPage = 25;

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'desc' ? 'asc' : 'desc'
    }));
  };

  const sortData = (data) => {
    return [...data].sort((a, b) => {
      const aVal = a[sortConfig.key];
      const bVal = b[sortConfig.key];
      
      if (typeof aVal === 'number' && typeof bVal === 'number') {
        return sortConfig.direction === 'asc' ? aVal - bVal : bVal - aVal;
      }
      return sortConfig.direction === 'asc' 
        ? (aVal > bVal ? 1 : -1) 
        : (aVal < bVal ? 1 : -1);
    });
  };

  if (!Array.isArray(tableData) || tableData.length === 0) {
    return <div style={{ padding: '20px', textAlign: 'center', color: '#666' }}>No broker analysis data available</div>;
  }

  const filteredData = tableData.filter(row => {
    const name = (row.brokerName || row.BrokerName || '').toString().toUpperCase();
    return name && name !== 'TOTAL';
  });

  const sortedData = sortData(filteredData);
  const totalPages = Math.ceil(sortedData.length / recordsPerPage);
  const displayData = sortedData.slice((currentPage - 1) * recordsPerPage, currentPage * recordsPerPage);

  const formatNumber = (value) => {
    if (typeof value === 'number') {
      return value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    }
    return value;
  };

  const formatInteger = (value) => {
    if (typeof value === 'number') {
      return parseInt(value, 10).toLocaleString('en-US');
    }
    return value;
  };

  return (
    <>
      <div className="broker-analysis-container" style={{ marginBottom: '20px' }}>
        <div style={{ 
          background: '#1e3a8a', 
          color: 'white', 
          padding: '16px', 
          borderRadius: '8px 8px 0 0', 
          fontWeight: 'bold', 
          fontSize: '16px'
        }}>
          Broker Activity Analysis - {filteredData.length} Active Brokers
        </div>
        <div style={{ overflowX: 'auto', borderRadius: '0 0 8px 8px' }}>
          <table style={{ 
            width: '100%', 
            borderCollapse: 'collapse',
            border: '1px solid #1e3a8a'
          }}>
            <thead>
              <tr style={{ background: '#1e3a8a', color: 'white' }}>
                <th style={{ padding: '12px', textAlign: 'center', borderRight: '1px solid #0f172a' }}>SN</th>
                <th style={{ padding: '12px', textAlign: 'center', cursor: 'pointer', borderRight: '1px solid #0f172a' }} onClick={() => handleSort('memberId')}>
                  Broker ID {sortConfig.key === 'memberId' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                </th>
                <th style={{ padding: '12px', cursor: 'pointer', borderRight: '1px solid #0f172a' }} onClick={() => handleSort('brokerName')}>
                  Broker {sortConfig.key === 'brokerName' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                </th>
                <th style={{ padding: '12px', textAlign: 'center', cursor: 'pointer', borderRight: '1px solid #0f172a' }} onClick={() => handleSort('buyQuantity')}>
                  Buy Qty {sortConfig.key === 'buyQuantity' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                </th>
                <th style={{ padding: '12px', textAlign: 'center', cursor: 'pointer', borderRight: '1px solid #0f172a' }} onClick={() => handleSort('sellQuantity')}>
                  Sell Qty {sortConfig.key === 'sellQuantity' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                </th>
                <th style={{ padding: '12px', textAlign: 'center', cursor: 'pointer', borderRight: '1px solid #0f172a' }} onClick={() => handleSort('netQuantity')}>
                  Net Qty {sortConfig.key === 'netQuantity' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                </th>
                <th style={{ padding: '12px', textAlign: 'center', cursor: 'pointer', borderRight: '1px solid #0f172a' }} onClick={() => handleSort('avgBuyRate')}>
                  Avg Buy {sortConfig.key === 'avgBuyRate' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                </th>
                <th style={{ padding: '12px', textAlign: 'center', cursor: 'pointer', borderRight: '1px solid #0f172a' }} onClick={() => handleSort('avgSellRate')}>
                  Avg Sell {sortConfig.key === 'avgSellRate' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                </th>
                <th style={{ padding: '12px', textAlign: 'center', cursor: 'pointer' }} onClick={() => handleSort('brokerActivity')}>
                  Activity {sortConfig.key === 'brokerActivity' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                </th>
              </tr>
            </thead>
            <tbody>
              {displayData.map((row, index) => {
                const brokerId = parseInt(row.memberId || row.brokerId || 0);
                const shouldHighlight = highlightToggles && highlightToggles.brk && brokerId && HIGHLIGHT_BROKER_IDS.has(brokerId);
                const activity = (row.brokerActivity || '').toString().toLowerCase();
                const isAccumulator = activity.includes('accum');
                
                return (
                  <tr 
                    key={index} 
                    className={shouldHighlight ? 'highlight-broker' : undefined}
                    style={{ 
                      background: isAccumulator ? '#f0fdf4' : '#fef2f2',
                      borderBottom: '1px solid #e5e7eb',
                      color: '#000000'
                    }}
                  >
                    <td style={{ padding: '10px', textAlign: 'center', color: '#000000' }}>{(currentPage - 1) * recordsPerPage + index + 1}</td>
                    <td style={{ padding: '10px', textAlign: 'center', color: '#000000', fontWeight: 'bold' }}>{row.memberId || row.brokerId || 'N/A'}</td>
                    <td style={{ padding: '10px', color: '#000000' }}><strong>{row.brokerName || row.BrokerName || 'N/A'}</strong></td>
                    <td style={{ padding: '10px', textAlign: 'right', color: '#059669' }}>{formatInteger(row.buyQuantity || 0)}</td>
                    <td style={{ padding: '10px', textAlign: 'right', color: '#dc2626' }}>{formatInteger(row.sellQuantity || 0)}</td>
                    <td style={{ padding: '10px', textAlign: 'right', fontWeight: 'bold', color: isAccumulator ? '#059669' : '#dc2626' }}>
                      {formatInteger(row.netQuantity || 0)}
                    </td>
                    <td style={{ padding: '10px', textAlign: 'right', color: '#000000' }}>{formatNumber(row.avgBuyRate || 0)}</td>
                    <td style={{ padding: '10px', textAlign: 'right', color: '#000000' }}>{formatNumber(row.avgSellRate || 0)}</td>
                    <td style={{ padding: '10px', textAlign: 'center', color: '#000000' }}>
                      <span style={{
                        display: 'inline-block',
                        padding: '4px 8px',
                        borderRadius: '4px',
                        fontSize: '12px',
                        fontWeight: 'bold',
                        background: isAccumulator ? '#dcfce7' : '#fee2e2',
                        color: isAccumulator ? '#166534' : '#991b1b'
                      }}>
                        {isAccumulator ? 'ACC' : 'DIS'}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
      {filteredData.length > recordsPerPage && (
        <div style={{ display: 'flex', justifyContent: 'center', gap: '8px', marginTop: '16px' }}>
          <button onClick={() => setCurrentPage(p => Math.max(p - 1, 1))} disabled={currentPage === 1} style={{ padding: '6px 12px', borderRadius: '4px', border: '1px solid #d1d5db', cursor: 'pointer' }}>Previous</button>
          {Array.from({ length: totalPages }, (_, i) => (
            <button
              key={i + 1}
              onClick={() => setCurrentPage(i + 1)}
              style={{
                padding: '6px 12px',
                borderRadius: '4px',
                border: '1px solid #d1d5db',
                background: currentPage === i + 1 ? '#1e3a8a' : 'transparent',
                color: currentPage === i + 1 ? 'white' : 'inherit',
                cursor: 'pointer'
              }}
            >
              {i + 1}
            </button>
          ))}
          <button onClick={() => setCurrentPage(p => Math.min(p + 1, totalPages))} disabled={currentPage === totalPages} style={{ padding: '6px 12px', borderRadius: '4px', border: '1px solid #d1d5db', cursor: 'pointer' }}>Next</button>
        </div>
      )}
    </>
  );
}

// TopAccumulatorsTableDesigned - Top Accumulators with modern green design
function TopAccumulatorsTableDesigned({ tableData, highlightToggles }) {
  const [sortConfig, setSortConfig] = useState({ key: 'accumulation', direction: 'desc' });
  const [currentPage, setCurrentPage] = useState(1);
  const recordsPerPage = 25;

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'desc' ? 'asc' : 'desc'
    }));
  };

  const sortData = (data) => {
    return [...data].sort((a, b) => {
      const aVal = a[sortConfig.key];
      const bVal = b[sortConfig.key];
      
      if (typeof aVal === 'number' && typeof bVal === 'number') {
        return sortConfig.direction === 'asc' ? aVal - bVal : bVal - aVal;
      }
      return sortConfig.direction === 'asc' 
        ? (aVal > bVal ? 1 : -1) 
        : (aVal < bVal ? 1 : -1);
    });
  };

  if (!Array.isArray(tableData) || tableData.length === 0) {
    return <div style={{ padding: '20px', textAlign: 'center', color: '#666' }}>No accumulator data available</div>;
  }

  const sortedData = sortData(tableData);
  const totalPages = Math.ceil(sortedData.length / recordsPerPage);
  const displayData = sortedData.slice((currentPage - 1) * recordsPerPage, currentPage * recordsPerPage);

  const totalAccumulation = sortedData.reduce((sum, row) => sum + (parseInt(row.accumulation || row.brokerHolding || 0)), 0);

  const formatNumber = (value) => {
    if (typeof value === 'number') {
      return value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    }
    return value;
  };

  const formatInteger = (value) => {
    if (typeof value === 'number') {
      return parseInt(value, 10).toLocaleString('en-US');
    }
    return value;
  };

  return (
    <>
      <div className="top-accumulators-container" style={{ marginBottom: '20px' }}>
        <div style={{ 
          background: '#2e7d32', 
          color: 'white', 
          padding: '16px', 
          borderRadius: '8px 8px 0 0',
          display: 'grid',
          gridTemplateColumns: 'repeat(4, 1fr)',
          gap: '16px',
          fontWeight: 'bold',
          fontSize: '14px'
        }}>
          <div><div style={{ opacity: 0.8, fontSize: '12px' }}>Total Accumulators</div><div style={{ fontSize: '18px', marginTop: '4px' }}>{tableData.length}</div></div>
          <div><div style={{ opacity: 0.8, fontSize: '12px' }}>Total Holdings</div><div style={{ fontSize: '18px', marginTop: '4px' }}>{formatInteger(totalAccumulation)}</div></div>
          <div><div style={{ opacity: 0.8, fontSize: '12px' }}>Avg Holdings</div><div style={{ fontSize: '18px', marginTop: '4px' }}>{formatInteger(totalAccumulation / tableData.length)}</div></div>
          <div><div style={{ opacity: 0.8, fontSize: '12px' }}>Symbol</div><div style={{ fontSize: '18px', marginTop: '4px' }}>{(tableData[0]?.scriptName || tableData[0]?.symbol || 'N/A').toUpperCase()}</div></div>
        </div>
        <div style={{ overflowX: 'auto', borderRadius: '0 0 8px 8px', border: '2px solid #2e7d32' }}>
          <table style={{ 
            width: '100%', 
            borderCollapse: 'collapse'
          }}>
            <thead>
              <tr style={{ background: '#2e7d32', color: 'white' }}>
                <th style={{ padding: '12px', textAlign: 'center', borderRight: '1px solid #1b5e20' }}>SN</th>
                <th style={{ padding: '12px', cursor: 'pointer', borderRight: '1px solid #1b5e20' }} onClick={() => handleSort('memberId')}>
                  Broker ID {sortConfig.key === 'memberId' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                </th>
                <th style={{ padding: '12px', cursor: 'pointer', borderRight: '1px solid #1b5e20' }} onClick={() => handleSort('brokerName')}>
                  Broker Name {sortConfig.key === 'brokerName' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                </th>
                <th style={{ padding: '12px', textAlign: 'center', cursor: 'pointer', borderRight: '1px solid #1b5e20' }} onClick={() => handleSort('accumulation')}>
                  Holdings {sortConfig.key === 'accumulation' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                </th>
                <th style={{ padding: '12px', textAlign: 'center', cursor: 'pointer', borderRight: '1px solid #1b5e20' }} onClick={() => handleSort('averagePrice')}>
                  Avg Price {sortConfig.key === 'averagePrice' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                </th>
                <th style={{ padding: '12px', textAlign: 'center', cursor: 'pointer' }} onClick={() => handleSort('pctOfBuys')}>
                  % of Buys {sortConfig.key === 'pctOfBuys' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                </th>
              </tr>
            </thead>
            <tbody>
              {displayData.map((row, index) => {
                const brokerId = parseInt(row.memberId || row.brokerId || 0);
                const shouldHighlight = highlightToggles && highlightToggles.topAcc && brokerId && HIGHLIGHT_BROKER_IDS.has(brokerId);
                
                return (
                  <tr 
                    key={index} 
                    className={shouldHighlight ? 'highlight-broker' : undefined}
                    style={{ 
                      background: (currentPage - 1) * recordsPerPage + index % 2 === 0 ? '#f1f8e9' : '#e8f5e8',
                      borderBottom: '1px solid #c5e1a5',
                      color: '#000000'
                    }}
                  >
                    <td style={{ padding: '10px', textAlign: 'center', color: '#000000' }}>{(currentPage - 1) * recordsPerPage + index + 1}</td>
                    <td style={{ padding: '10px', textAlign: 'center', fontWeight: 'bold', color: '#000000' }}>{row.memberId || row.brokerId || 'N/A'}</td>
                    <td style={{ padding: '10px', color: '#000000' }}><strong>{row.brokerName || 'N/A'}</strong></td>
                    <td style={{ padding: '10px', textAlign: 'right', color: '#2e7d32', fontWeight: 'bold' }}>{formatInteger(row.accumulation || row.brokerHolding || 0)}</td>
                    <td style={{ padding: '10px', textAlign: 'right', color: '#000000' }}>{formatNumber(row.averagePrice || row.AvgBuyPriceTopN || 0)}</td>
                    <td style={{ padding: '10px', textAlign: 'center', color: '#000000' }}>{formatNumber(row.pctOfBuys || 0)}%</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
      {tableData.length > recordsPerPage && (
        <div style={{ display: 'flex', justifyContent: 'center', gap: '8px', marginTop: '16px' }}>
          <button onClick={() => setCurrentPage(p => Math.max(p - 1, 1))} disabled={currentPage === 1} style={{ padding: '6px 12px', borderRadius: '4px', border: '1px solid #d1d5db', cursor: 'pointer' }}>Previous</button>
          {Array.from({ length: totalPages }, (_, i) => (
            <button
              key={i + 1}
              onClick={() => setCurrentPage(i + 1)}
              style={{
                padding: '6px 12px',
                borderRadius: '4px',
                border: '1px solid #d1d5db',
                background: currentPage === i + 1 ? '#2e7d32' : 'transparent',
                color: currentPage === i + 1 ? 'white' : 'inherit',
                cursor: 'pointer'
              }}
            >
              {i + 1}
            </button>
          ))}
          <button onClick={() => setCurrentPage(p => Math.min(p + 1, totalPages))} disabled={currentPage === totalPages} style={{ padding: '6px 12px', borderRadius: '4px', border: '1px solid #d1d5db', cursor: 'pointer' }}>Next</button>
        </div>
      )}
    </>
  );
}

// TopDistributorsTableDesigned - Top Distributors with modern red design
function TopDistributorsTableDesigned({ tableData, highlightToggles }) {
  const [sortConfig, setSortConfig] = useState({ key: 'distribution', direction: 'desc' });
  const [currentPage, setCurrentPage] = useState(1);
  const recordsPerPage = 25;

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'desc' ? 'asc' : 'desc'
    }));
  };

  const sortData = (data) => {
    return [...data].sort((a, b) => {
      const aVal = a[sortConfig.key];
      const bVal = b[sortConfig.key];
      
      if (typeof aVal === 'number' && typeof bVal === 'number') {
        return sortConfig.direction === 'asc' ? aVal - bVal : bVal - aVal;
      }
      return sortConfig.direction === 'asc' 
        ? (aVal > bVal ? 1 : -1) 
        : (aVal < bVal ? 1 : -1);
    });
  };

  if (!Array.isArray(tableData) || tableData.length === 0) {
    return <div style={{ padding: '20px', textAlign: 'center', color: '#666' }}>No distributor data available</div>;
  }

  const sortedData = sortData(tableData);
  const totalPages = Math.ceil(sortedData.length / recordsPerPage);
  const displayData = sortedData.slice((currentPage - 1) * recordsPerPage, currentPage * recordsPerPage);

  const totalDistribution = sortedData.reduce((sum, row) => sum + (parseInt(row.distribution || row.brokerRelease || 0)), 0);

  const formatNumber = (value) => {
    if (typeof value === 'number') {
      return value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    }
    return value;
  };

  const formatInteger = (value) => {
    if (typeof value === 'number') {
      return parseInt(value, 10).toLocaleString('en-US');
    }
    return value;
  };

  return (
    <>
      <div className="top-distributors-container" style={{ marginBottom: '20px' }}>
        <div style={{ 
          background: '#d32f2f', 
          color: 'white', 
          padding: '16px', 
          borderRadius: '8px 8px 0 0',
          display: 'grid',
          gridTemplateColumns: 'repeat(4, 1fr)',
          gap: '16px',
          fontWeight: 'bold',
          fontSize: '14px'
        }}>
          <div><div style={{ opacity: 0.8, fontSize: '12px' }}>Total Distributors</div><div style={{ fontSize: '18px', marginTop: '4px' }}>{tableData.length}</div></div>
          <div><div style={{ opacity: 0.8, fontSize: '12px' }}>Total Release</div><div style={{ fontSize: '18px', marginTop: '4px' }}>-{formatInteger(totalDistribution)}</div></div>
          <div><div style={{ opacity: 0.8, fontSize: '12px' }}>Avg Release</div><div style={{ fontSize: '18px', marginTop: '4px' }}>-{formatInteger(totalDistribution / tableData.length)}</div></div>
          <div><div style={{ opacity: 0.8, fontSize: '12px' }}>Symbol</div><div style={{ fontSize: '18px', marginTop: '4px' }}>{(tableData[0]?.scriptName || tableData[0]?.symbol || 'N/A').toUpperCase()}</div></div>
        </div>
        <div style={{ overflowX: 'auto', borderRadius: '0 0 8px 8px', border: '2px solid #d32f2f' }}>
          <table style={{ 
            width: '100%', 
            borderCollapse: 'collapse'
          }}>
            <thead>
              <tr style={{ background: '#d32f2f', color: 'white' }}>
                <th style={{ padding: '12px', textAlign: 'center', borderRight: '1px solid #b71c1c' }}>SN</th>
                <th style={{ padding: '12px', cursor: 'pointer', borderRight: '1px solid #b71c1c' }} onClick={() => handleSort('memberId')}>
                  Broker ID {sortConfig.key === 'memberId' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                </th>
                <th style={{ padding: '12px', cursor: 'pointer', borderRight: '1px solid #b71c1c' }} onClick={() => handleSort('brokerName')}>
                  Broker Name {sortConfig.key === 'brokerName' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                </th>
                <th style={{ padding: '12px', textAlign: 'center', cursor: 'pointer', borderRight: '1px solid #b71c1c' }} onClick={() => handleSort('distribution')}>
                  Release {sortConfig.key === 'distribution' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                </th>
                <th style={{ padding: '12px', textAlign: 'center', cursor: 'pointer', borderRight: '1px solid #b71c1c' }} onClick={() => handleSort('averagePrice')}>
                  Avg Price {sortConfig.key === 'averagePrice' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                </th>
                <th style={{ padding: '12px', textAlign: 'center', cursor: 'pointer' }} onClick={() => handleSort('pctOfSells')}>
                  % of Sells {sortConfig.key === 'pctOfSells' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                </th>
              </tr>
            </thead>
            <tbody>
              {displayData.map((row, index) => {
                const brokerId = parseInt(row.memberId || row.brokerId || 0);
                const shouldHighlight = highlightToggles && highlightToggles.topDiss && brokerId && HIGHLIGHT_BROKER_IDS.has(brokerId);
                
                return (
                  <tr 
                    key={index} 
                    className={shouldHighlight ? 'highlight-broker' : undefined}
                    style={{ 
                      background: (currentPage - 1) * recordsPerPage + index % 2 === 0 ? '#ffebee' : '#ffcdd2',
                      borderBottom: '1px solid #ffab91',
                      color: '#000000'
                    }}
                  >
                    <td style={{ padding: '10px', textAlign: 'center', color: '#000000' }}>{(currentPage - 1) * recordsPerPage + index + 1}</td>
                    <td style={{ padding: '10px', textAlign: 'center', fontWeight: 'bold', color: '#000000' }}>{row.memberId || row.brokerId || 'N/A'}</td>
                    <td style={{ padding: '10px', color: '#000000' }}><strong>{row.brokerName || 'N/A'}</strong></td>
                    <td style={{ padding: '10px', textAlign: 'right', color: '#d32f2f', fontWeight: 'bold' }}>-{formatInteger(row.distribution || row.brokerRelease || 0)}</td>
                    <td style={{ padding: '10px', textAlign: 'right', color: '#000000' }}>{formatNumber(row.averagePrice || row.AvgSellPriceTopN || 0)}</td>
                    <td style={{ padding: '10px', textAlign: 'center', color: '#000000' }}>{formatNumber(row.pctOfSells || 0)}%</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
      {tableData.length > recordsPerPage && (
        <div style={{ display: 'flex', justifyContent: 'center', gap: '8px', marginTop: '16px' }}>
          <button onClick={() => setCurrentPage(p => Math.max(p - 1, 1))} disabled={currentPage === 1} style={{ padding: '6px 12px', borderRadius: '4px', border: '1px solid #d1d5db', cursor: 'pointer' }}>Previous</button>
          {Array.from({ length: totalPages }, (_, i) => (
            <button
              key={i + 1}
              onClick={() => setCurrentPage(i + 1)}
              style={{
                padding: '6px 12px',
                borderRadius: '4px',
                border: '1px solid #d1d5db',
                background: currentPage === i + 1 ? '#d32f2f' : 'transparent',
                color: currentPage === i + 1 ? 'white' : 'inherit',
                cursor: 'pointer'
              }}
            >
              {i + 1}
            </button>
          ))}
          <button onClick={() => setCurrentPage(p => Math.min(p + 1, totalPages))} disabled={currentPage === totalPages} style={{ padding: '6px 12px', borderRadius: '4px', border: '1px solid #d1d5db', cursor: 'pointer' }}>Next</button>
        </div>
      )}
    </>
  );
}

// AccDisTable component for displaying accumulation and distribution data side by side
function AccDisTable({ marketData, theme, topAccumulatorsData, topDistributorsData, highlightToggles }) {
  const [sortConfig, setSortConfig] = useState({ 
    accumulation: { key: 'brokerHolding', direction: 'desc' },
    distribution: { key: 'brokerRelease', direction: 'desc' }
  });
  const [exportingPdf, setExportingPdf] = useState(false);
  const accDisRef = useRef(null);

  // Process data to get accumulation and distribution from Top ACC and Top Diss tabs
  const processAccDisData = () => {
    
    // Get script name from either dataset
    const scriptName = (topAccumulatorsData.length > 0 ? topAccumulatorsData[0].scriptName : null) ||
                      (topDistributorsData.length > 0 ? topDistributorsData[0].scriptName : null) ||
                      'N/A';
    
    // Get LTP from market summary or final analysis
    let ltp = 'N/A';
    if (marketData.marketSummary && marketData.marketSummary.length > 0) {
      ltp = marketData.marketSummary[0].averagePrice || 'N/A';
    } else if (marketData.finalAnalysis && marketData.finalAnalysis.length > 0) {
      ltp = marketData.finalAnalysis[0].averagePrice || 'N/A';
    }

    // Process accumulation data from topAccumulators
    const accumulationData = (topAccumulatorsData || []).map((row, index) => ({
      symbol: row.scriptName || scriptName,
      buyerBroker: parseInt(row.memberId || row.brokerId || 0) || (index + 1), // Fallback to index if 0
      brokerHolding: parseInt(row.accumulation || 0),
  // Prefer row.averagePrice -> AvgBuyPriceTopN -> avgBuyRate
  avgBuyRate: parseFloat((row.averagePrice !== undefined ? row.averagePrice : (row.AvgBuyPriceTopN !== undefined ? row.AvgBuyPriceTopN : row.avgBuyRate)) || 0)
    }));

    // Process distribution data from topDistributors
    const distributionData = (topDistributorsData || []).map((row, index) => ({
      symbol: row.scriptName || scriptName,
      sellerBroker: parseInt(row.memberId || row.brokerId || 0) || (index + 1), // Fallback to index if 0
      brokerRelease: parseInt(row.distribution || 0), // This should be negative
  // Prefer row.averagePrice -> AvgSellPriceTopN -> avgSellRate
  avgSellRate: parseFloat((row.averagePrice !== undefined ? row.averagePrice : (row.AvgSellPriceTopN !== undefined ? row.AvgSellPriceTopN : row.avgSellRate)) || 0)
    }));

    // Calculate totals
    const totalBrokerHolding = accumulationData.reduce((sum, row) => sum + row.brokerHolding, 0);
    const totalBrokerRelease = Math.abs(distributionData.reduce((sum, row) => sum + row.brokerRelease, 0));
    const totalBrokers = accumulationData.length;
    const totalReleasingBrokers = distributionData.length;

    return {
      scriptName,
      ltp,
      totalBrokerHolding,
      totalBrokerRelease,
      totalBrokers,
      totalReleasingBrokers,
      accumulationData,
      distributionData
    };
  };

  const handleSort = (tableType, key) => {
    setSortConfig(prev => ({
      ...prev,
      [tableType]: {
        key,
        direction: prev[tableType].key === key && prev[tableType].direction === 'asc' ? 'desc' : 'asc'
      }
    }));
  };

  const sortData = (data, tableType) => {
    const config = sortConfig[tableType];
    return [...data].sort((a, b) => {
      const aVal = a[config.key];
      const bVal = b[config.key];
      
      if (config.direction === 'asc') {
        return aVal > bVal ? 1 : -1;
      } else {
        return aVal < bVal ? 1 : -1;
      }
    });
  };

  const data = processAccDisData();

  if (!data.accumulationData.length && !data.distributionData.length) {
    return (
      <div style={{ 
        padding: '20px', 
        textAlign: 'center', 
        color: '#666',
        backgroundColor: '#f5f5f5',
        borderRadius: '8px',
        margin: '20px'
      }}>
        <h3>No accumulation/distribution data available</h3>
        <p>Please ensure you have:</p>
        <ul style={{ textAlign: 'left', display: 'inline-block' }}>
          <li>Selected a script name</li>
          <li>Chosen a date range</li>
          <li>Fetched data from the server</li>
        </ul>
      </div>
    );
  }

  const formatNumber = (value) => {
    if (typeof value === 'number') {
      return value.toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      });
    }
    return value;
  };

  const formatInteger = (value) => {
    if (typeof value === 'number') {
      return parseInt(value, 10).toLocaleString('en-US');
    }
    return value;
  };

  const sortedAccumulationData = sortData(data.accumulationData, 'accumulation');
  const sortedDistributionData = sortData(data.distributionData, 'distribution');

  const getCurrentTime = () => {
    const now = new Date();
    return now.toLocaleTimeString('en-US', { 
      hour: 'numeric', 
      minute: '2-digit',
      hour12: true 
    });
  };

  // Export combined Acc/Dis data to CSV (Excel-friendly)
  const exportAccDisToCSV = () => {
    const acc = data.accumulationData || [];
    const diss = data.distributionData || [];
    const maxLen = Math.max(acc.length, diss.length);

    const csvRows = [];
    // Header: left block = Accumulation, right block = Distribution
    csvRows.push([
      'Acc_SN','Acc_Symbol','BuyerBroker','BrokerHolding','Acc_AvgPrice',
      '',
      'Diss_SN','Diss_Symbol','SellerBroker','BrokerRelease','Diss_AvgPrice'
    ].join(','));

    const csvEscape = (v) => {
      if (v === null || v === undefined) return '';
      const s = typeof v === 'number' ? v.toString() : String(v);
      // escape quotes
      if (s.includes(',') || s.includes('\"') || s.includes('\n')) {
        return '"' + s.replace(/"/g, '""') + '"';
      }
      return s;
    };

    for (let i = 0; i < maxLen; i++) {
      const a = acc[i];
      const d = diss[i];
      const row = [
        csvEscape(a ? (i + 1) : ''),
        csvEscape(a ? a.symbol : ''),
        csvEscape(a ? a.buyerBroker : ''),
        csvEscape(a ? a.brokerHolding : ''),
        csvEscape(a ? (typeof a.avgBuyRate === 'number' ? a.avgBuyRate : a.avgBuyRate) : ''),
        '',
        csvEscape(d ? (i + 1) : ''),
        csvEscape(d ? d.symbol : ''),
        csvEscape(d ? d.sellerBroker : ''),
        csvEscape(d ? d.brokerRelease : ''),
        csvEscape(d ? (typeof d.avgSellRate === 'number' ? d.avgSellRate : d.avgSellRate) : '')
      ];
      csvRows.push(row.join(','));
    }

    const csvContent = csvRows.join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    const now = new Date();
    const stamp = now.toISOString().replace(/[:.]/g, '-');
    a.href = url;
    a.setAttribute('download', `acc_dis_export_${stamp}.csv`);
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // Export Acc/Dis to PDF (capture UI as image and insert into PDF)
  const exportAccDisToPDF = async () => {
    if (!accDisRef.current) {
      alert('Cannot capture UI: reference not available');
      return;
    }
    setExportingPdf(true);
    try {
      const [{ default: html2canvas }, { jsPDF }] = await Promise.all([import('html2canvas'), import('jspdf')]);

      const orig = accDisRef.current;
      // Clone the node so we can remove controls and adjust styles without affecting live UI
      const clone = orig.cloneNode(true);
      // Remove controls from clone (buttons, overlays)
      const controls = clone.querySelector('.acc-dis-controls');
      if (controls && controls.parentNode) controls.parentNode.removeChild(controls);

      // Place clone off-screen to allow html2canvas to render it without scrollbars
      clone.style.position = 'fixed';
      clone.style.left = '-9999px';
      clone.style.top = '0';
      clone.style.zIndex = '99999';
      document.body.appendChild(clone);

      try {
        const scale = Math.min(3, (window.devicePixelRatio || 1) * 2);
        const canvas = await html2canvas(clone, { scale, useCORS: true, backgroundColor: '#ffffff' });

        // Split canvas into multiple pages if it's taller than a single A4-like page
        const pageWidth = canvas.width;
        // Use A4 aspect ratio (210 x 297 mm) to determine page height in canvas pixels
        const pageHeight = Math.floor(pageWidth * (297 / 210));
        const totalHeight = canvas.height;

        const pdf = new jsPDF({ unit: 'px', format: [pageWidth, pageHeight] });

        let yPos = 0;
        let pageIndex = 0;
        while (yPos < totalHeight) {
          const sliceHeight = Math.min(pageHeight, totalHeight - yPos);
          // create temporary canvas for this page slice
          const pageCanvas = document.createElement('canvas');
          pageCanvas.width = pageWidth;
          pageCanvas.height = sliceHeight;
          const ctx = pageCanvas.getContext('2d');
          // white background
          ctx.fillStyle = '#ffffff';
          ctx.fillRect(0, 0, pageWidth, sliceHeight);
          // draw the slice from the original canvas
          ctx.drawImage(canvas, 0, yPos, pageWidth, sliceHeight, 0, 0, pageWidth, sliceHeight);

          const imgData = pageCanvas.toDataURL('image/png');

          if (pageIndex === 0) {
            pdf.addImage(imgData, 'PNG', 0, 0, pageWidth, sliceHeight);
          } else {
            pdf.addPage([pageWidth, pageHeight]);
            pdf.addImage(imgData, 'PNG', 0, 0, pageWidth, sliceHeight);
          }

          pageIndex += 1;
          yPos += sliceHeight;
        }

        const now = new Date();
        const stamp = now.toISOString().replace(/[:.]/g, '-');
        pdf.save(`acc_dis_${stamp}.pdf`);
      } finally {
        // Remove clone after capture
        if (clone && clone.parentNode) clone.parentNode.removeChild(clone);
      }
    } catch (err) {
      console.error('Failed to export PDF', err);
      alert('Export to PDF failed. See console for details.');
    } finally {
      setExportingPdf(false);
    }
  };

  return (
    <div className="acc-dis-container" ref={accDisRef} style={{ position: 'relative' }}>
      <div className="acc-dis-controls" style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 8 }}>
        <button onClick={exportAccDisToCSV} style={{ padding: '8px 12px', borderRadius: 6, cursor: 'pointer' }}>Export Acc/Dis (CSV)</button>
        <button onClick={exportAccDisToPDF} disabled={exportingPdf} style={{ padding: '8px 12px', borderRadius: 6, cursor: exportingPdf ? 'not-allowed' : 'pointer' }}>{exportingPdf ? 'Exporting...' : 'Export to PDF'}</button>
      </div>
      <div style={{ 
        display: 'flex', 
        gap: '20px', 
        marginTop: '20px',
        flexWrap: 'wrap'
      }}>
      {/* Accumulation Table */}
      <div className="accumulation-table" style={{ 
        flex: 1, 
        minWidth: '400px',
        marginBottom: '20px'
      }}>
        <div className="acc-dis-header buyer" style={{ background: '#2e7d32', color: 'white', padding: '12px', borderRadius: '8px 8px 0 0', fontWeight: 'bold', fontSize: '16px' }}>
          <div className="acc-dis-header-row">
            <div className="acc-dis-header-item">Sym: <span className="acc-dis-header-value">{data.scriptName}</span></div>
            <div className="acc-dis-header-item">LTP: <span className="acc-dis-header-value">{formatNumber(data.ltp)}</span></div>
            <div className="acc-dis-header-item">Brk Hold: <span className="acc-dis-header-value">{formatInteger(data.totalBrokerHolding)}</span></div>
            <div className="acc-dis-header-item">Brks: <span className="acc-dis-header-value">{data.totalBrokers} Nos</span></div>
          </div>
        </div>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ 
            width: '100%', 
            borderCollapse: 'collapse',
            border: '2px solid #2e7d32',
            minWidth: '350px'
          }}>
          <thead>
            <tr style={{ background: '#2e7d32', color: 'white' }}>
                  <th style={{ padding: '8px', border: '1px solid #fff' }}>SN</th>
                  <th style={{ padding: '8px', border: '1px solid #fff', cursor: 'pointer' }}
                      onClick={() => handleSort('accumulation', 'symbol')}>
                    Symbol {sortConfig.accumulation.key === 'symbol' && (sortConfig.accumulation.direction === 'asc' ? '↑' : '↓')}
                  </th>
                  <th style={{ padding: '8px', border: '1px solid #fff', cursor: 'pointer' }}
                      onClick={() => handleSort('accumulation', 'buyerBroker')}>
                    Buyer Broker {sortConfig.accumulation.key === 'buyerBroker' && (sortConfig.accumulation.direction === 'asc' ? '↑' : '↓')}
                  </th>
                  <th style={{ padding: '8px', border: '1px solid #fff', cursor: 'pointer' }}
                      onClick={() => handleSort('accumulation', 'brokerHolding')}>
                    Broker Holding {sortConfig.accumulation.key === 'brokerHolding' && (sortConfig.accumulation.direction === 'asc' ? '↑' : '↓')}
                  </th>
                  <th style={{ padding: '8px', border: '1px solid #fff', cursor: 'pointer' }}
                      onClick={() => handleSort('accumulation', 'avgBuyRate')}>
                    Avg Price {sortConfig.accumulation.key === 'avgBuyRate' && (sortConfig.accumulation.direction === 'asc' ? '↑' : '↓')}
                  </th>
            </tr>
          </thead>
          <tbody>
            {sortedAccumulationData.map((row, index) => {
              const brokerId = parseInt(row.buyerBroker, 10);
              const shouldHighlight = highlightToggles && highlightToggles.topAcc && brokerId && HIGHLIGHT_BROKER_IDS.has(brokerId);
              return (
                <tr key={index} className={shouldHighlight ? 'highlight-broker' : undefined} style={{ background: index % 2 === 0 ? '#f1f8e9' : '#e8f5e8' }}>
                  <td style={{ padding: '8px', border: '1px solid #2e7d32', textAlign: 'center' }}>{index + 1}</td>
                  <td style={{ padding: '8px', border: '1px solid #2e7d32', textAlign: 'center' }}>{row.symbol}</td>
                  <td style={{ padding: '8px', border: '1px solid #2e7d32', textAlign: 'center' }}>{row.buyerBroker}</td>
                  <td style={{ padding: '8px', border: '1px solid #2e7d32', textAlign: 'center' }}>{formatInteger(row.brokerHolding)}</td>
                  <td style={{ padding: '8px', border: '1px solid #2e7d32', textAlign: 'center' }}>{formatNumber(row.avgBuyRate)}</td>
                </tr>
              );
            })}
          </tbody>
          </table>
        </div>
      </div>

      {/* Distribution Table */}
      <div className="distribution-table" style={{ 
        flex: 1, 
        minWidth: '400px',
        marginBottom: '20px'
      }}>
        <div className="acc-dis-header seller" style={{ background: '#d32f2f', color: 'white', padding: '12px', borderRadius: '8px 8px 0 0', fontWeight: 'bold', fontSize: '16px' }}>
          <div className="acc-dis-header-row">
            <div className="acc-dis-header-item">Sym: <span className="acc-dis-header-value">{data.scriptName}</span></div>
            <div className="acc-dis-header-item">LTP: <span className="acc-dis-header-value">{formatNumber(data.ltp)}</span></div>
            <div className="acc-dis-header-item">Brk Release: <span className="acc-dis-header-value">-{formatInteger(data.totalBrokerRelease)}</span></div>
            <div className="acc-dis-header-item">Brks: <span className="acc-dis-header-value">{data.totalReleasingBrokers} Nos</span></div>
          </div>
        </div>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ 
            width: '100%', 
            borderCollapse: 'collapse',
            border: '2px solid #d32f2f',
            minWidth: '350px'
          }}>
          <thead>
            <tr style={{ background: '#d32f2f', color: 'white' }}>
              <th style={{ padding: '8px', border: '1px solid #fff' }}>SN</th>
              <th style={{ padding: '8px', border: '1px solid #fff', cursor: 'pointer' }}
                  onClick={() => handleSort('distribution', 'symbol')}>
                Symbol {sortConfig.distribution.key === 'symbol' && (sortConfig.distribution.direction === 'asc' ? '↑' : '↓')}
              </th>
              <th style={{ padding: '8px', border: '1px solid #fff', cursor: 'pointer' }}
                  onClick={() => handleSort('distribution', 'sellerBroker')}>
                Seller Broker {sortConfig.distribution.key === 'sellerBroker' && (sortConfig.distribution.direction === 'asc' ? '↑' : '↓')}
              </th>
              <th style={{ padding: '8px', border: '1px solid #fff', cursor: 'pointer' }}
                  onClick={() => handleSort('distribution', 'brokerRelease')}>
                Broker Release {sortConfig.distribution.key === 'brokerRelease' && (sortConfig.distribution.direction === 'asc' ? '↑' : '↓')}
              </th>
              <th style={{ padding: '8px', border: '1px solid #fff', cursor: 'pointer' }}
                  onClick={() => handleSort('distribution', 'avgSellRate')}>
                Avg Price {sortConfig.distribution.key === 'avgSellRate' && (sortConfig.distribution.direction === 'asc' ? '↑' : '↓')}
              </th>
            </tr>
          </thead>
          <tbody>
            {sortedDistributionData.map((row, index) => {
              const brokerId = parseInt(row.sellerBroker, 10);
              const shouldHighlight = highlightToggles && highlightToggles.topDiss && brokerId && HIGHLIGHT_BROKER_IDS.has(brokerId);
              return (
                <tr key={index} className={shouldHighlight ? 'highlight-broker' : undefined} style={{ background: index % 2 === 0 ? '#ffebee' : '#ffcdd2' }}>
                  <td style={{ padding: '8px', border: '1px solid #d32f2f', textAlign: 'center' }}>{index + 1}</td>
                  <td style={{ padding: '8px', border: '1px solid #d32f2f', textAlign: 'center' }}>{row.symbol}</td>
                  <td style={{ padding: '8px', border: '1px solid #d32f2f', textAlign: 'center' }}>{row.sellerBroker}</td>
                  <td style={{ padding: '8px', border: '1px solid #d32f2f', textAlign: 'center' }}>{formatInteger(row.brokerRelease)}</td>
                  <td style={{ padding: '8px', border: '1px solid #d32f2f', textAlign: 'center' }}>{formatNumber(row.avgSellRate)}</td>
                </tr>
              );
            })}
          </tbody>
          </table>
        </div>
      </div>
      </div>
      <div style={{ 
        textAlign: 'right', 
        marginTop: '10px', 
        fontSize: '14px', 
        color: '#666',
        fontStyle: 'italic'
      }}>
        {getCurrentTime()}
      </div>
      {exportingPdf && (
        <div className="export-overlay">Preparing PDF... Please wait</div>
      )}
    </div>
  );
}

function App() {
  const [form, setForm] = useState({
    ScriptName: '',
    StartDate: '',
    EndDate: '',
    buyerMemberId: 0,
    TopNRows: 10
  });
  const [marketData, setMarketData] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState(FA_TAB_INDEX);
  const [currentPage, setCurrentPage] = useState(1);
  const [recordsPerPage, setRecordsPerPage] = useState(RECORDS_PER_PAGE_OPTIONS[0]);
  const [showFilters, setShowFilters] = useState(false);
  const [theme, setTheme] = useState('minimalist-dark');
  const [activeRange, setActiveRange] = useState(null);
  const [accumDistribDiffByRange, setAccumDistribDiffByRange] = useState({});
  const [accumSummaryLoading, setAccumSummaryLoading] = useState(false);
  const [abortController, setAbortController] = useState(null);
  const [highlightToggles, setHighlightToggles] = useState({ brk: true, topAcc: true, topDiss: true });
  const [topBrokersOpen, setTopBrokersOpen] = useState(false);
  /** Last Traded Price for header (from algo API); null = unknown / not loaded */
  const [headerLtp, setHeaderLtp] = useState(null);
  const [headerLtpLoading, setHeaderLtpLoading] = useState(false);
  const [nepseIndex, setNepseIndex] = useState(null);
  const [nepseIndexMeta, setNepseIndexMeta] = useState({ asOfText: '', marketStatus: '', change: null, perChange: null });
  const [nepseIndexError, setNepseIndexError] = useState('');
  const [isNoticeDismissed, setIsNoticeDismissed] = useState(false);

  useEffect(() => {
    const script = (form.ScriptName || '').trim();
    if (!script) {
      setHeaderLtp(null);
      setHeaderLtpLoading(false);
      return;
    }
    const ac = new AbortController();
    let pollId;

    const fetchHeaderLtp = async () => {
      setHeaderLtpLoading(true);
      try {
        // Prefer NEPSE live market LTP for tighter parity with live screen.
        const liveResp = await fetch(`${SERVER_API_BASE}/api/nepse-ltp/${encodeURIComponent(script)}`, { signal: ac.signal });
        if (liveResp.ok) {
          const liveData = await liveResp.json();
          const liveLtp = Number(liveData?.ltp);
          if (Number.isFinite(liveLtp)) {
            setHeaderLtp(liveLtp);
            return;
          }
        }

        // Fallback to algo API signal LTP if live endpoint misses symbol.
        const q = new URLSearchParams({ min_pct: '0' });
        if (form.EndDate) q.set('end_date', form.EndDate);
        const signalResp = await fetch(`${ALGO_API_BASE}/api/signal/${encodeURIComponent(script)}?${q}`, { signal: ac.signal });
        if (!signalResp.ok) throw new Error(`Signal HTTP ${signalResp.status}`);
        const signalData = await signalResp.json();
        const fallbackLtp = Number(signalData?.ltp);
        setHeaderLtp(Number.isFinite(fallbackLtp) ? fallbackLtp : null);
      } catch {
        if (!ac.signal.aborted) setHeaderLtp(null);
      } finally {
        if (!ac.signal.aborted) setHeaderLtpLoading(false);
      }
    };

    const runAndSchedule = async () => {
      await fetchHeaderLtp();
      if (!ac.signal.aborted) {
        pollId = setTimeout(runAndSchedule, 60000);
      }
    };

    runAndSchedule();

    return () => {
      ac.abort();
      if (pollId) clearTimeout(pollId);
    };
  }, [form.ScriptName, form.EndDate]);

  const getNepalMarketClock = () => {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: 'Asia/Kathmandu',
      hour12: false,
      weekday: 'short',
      hour: '2-digit',
      minute: '2-digit'
    }).formatToParts(new Date());

    const valueByType = Object.fromEntries(parts.map((part) => [part.type, part.value]));
    const dayMap = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 };

    return {
      day: dayMap[valueByType.weekday] ?? -1,
      minutes: (Number(valueByType.hour) * 60) + Number(valueByType.minute)
    };
  };

  const isMarketOpenNow = () => {
    const now = getNepalMarketClock();
    const isWeekday = now.day >= 1 && now.day <= 5;
    const isInSession = now.minutes >= (11 * 60) && now.minutes <= (15 * 60);
    return isWeekday && isInSession;
  };

  const formatNepalAsOf = (isoString) => {
    const parsed = isoString ? new Date(isoString) : new Date();
    const dt = Number.isNaN(parsed.getTime()) ? new Date() : parsed;
    const dateText = new Intl.DateTimeFormat('en-US', {
      timeZone: 'Asia/Kathmandu',
      month: 'short',
      day: 'numeric'
    }).format(dt);
    const timeText = new Intl.DateTimeFormat('en-US', {
      timeZone: 'Asia/Kathmandu',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    }).format(dt);
    return { dateText, timeText };
  };

  useEffect(() => {
    let timerId;
    let disposed = false;

    const fetchNepseIndex = async () => {
      try {
        const response = await fetch(`${SERVER_API_BASE}/api/nepse-index`);
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }
        const data = await response.json();
        const value = Number(data?.value);
        if (Number.isFinite(value)) {
          const marketStatus = data?.marketStatus || (isMarketOpenNow() ? 'MARKET OPEN' : 'MARKET CLOSED');
          setNepseIndex(value);
          setNepseIndexMeta({
            asOfText: formatNepalAsOf(data?.asOf),
            marketStatus,
            change: Number.isFinite(Number(data?.change)) ? Number(data.change) : null,
            perChange: Number.isFinite(Number(data?.perChange)) ? Number(data.perChange) : null
          });
          setNepseIndexError('');
        } else {
          throw new Error('Invalid index value');
        }
      } catch (err) {
        setNepseIndexError('NEPSE index unavailable');
      }
    };

    const scheduleLoop = async () => {
      await fetchNepseIndex();
      if (disposed) return;
      timerId = setTimeout(scheduleLoop, isMarketOpenNow() ? 60000 : 300000);
    };

    scheduleLoop();

    return () => {
      disposed = true;
      if (timerId) clearTimeout(timerId);
    };
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    let newValue = value;
    if ((name === 'StartDate' || name === 'EndDate') && value) {
      const d = new Date(value);
      if (d.getDay() === 0 || d.getDay() === 6) {
        newValue = getMostRecentTradingDay(value);
      }
    }
    setForm((prev) => ({ ...prev, [name]: newValue }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    fetchData(form, activeRange);
  };

  const handleStop = () => {
    // Always cancel any ongoing request
    if (abortController) {
      abortController.abort();
      setAbortController(null);
    }
    
    // Always reset loading states and show stop message
    setLoading(false);
    setAccumSummaryLoading(false);
    setError('Connection stopped by user');

    
    // Don't clear existing data when stopping
    // This allows users to stop a request but keep viewing previous data
  };

  // Filter out empty tables and keep all result sets
  const filteredTables = Object.entries(marketData)
    .filter(([key, value]) => Array.isArray(value) && value.length > 0)
    .filter(([key]) => key !== 'completionMessage')
    .map(([key, value], idx) => ({ table: value, idx, key }));

  // Build displayedTables from filteredTables. If both topAccumulators and topDistributors
  // exist, insert a synthetic 'accumulationDistribution' entry so there's a dedicated
  // Acc/Dis tab while keeping Top ACC and Top Diss separate.
  const displayedTables = (() => {
    const map = Object.fromEntries(filteredTables.map(ft => [ft.key, ft]));
    const result = [...filteredTables];
    
    // Ensure FA (finalAnalysis) is always present so it can be the default tab
    if (!map.finalAnalysis) {
      result.push({ table: [], idx: -3, key: 'finalAnalysis' });
    }

    if (map.topAccumulators && map.topDistributors) {
      // insert accumulationDistribution right after topAccumulators if not present
      const exists = result.find(r => r.key === 'accumulationDistribution');
      if (!exists) {
        const accTable = map.topAccumulators.table || [];
        const dissTable = map.topDistributors.table || [];
        // synthetic combined table - Acc/Dis component reads topAccumulators/topDistributors directly
        const insertIdx = result.findIndex(r => r.key === 'topAccumulators');
        const synthetic = { table: [], idx: -1, key: 'accumulationDistribution' };
        // place after topAccumulators, or at end if not found
        if (insertIdx >= 0) result.splice(insertIdx + 1, 0, synthetic);
        else result.push(synthetic);
      }
    }
    
    // insert algoEngine tab at the end if not present
    const algoExists = result.find(r => r.key === 'algoEngine');
    if (!algoExists) {
      result.push({ table: [], idx: -2, key: 'algoEngine' });
    }
    
    // Sort by KEY_LABEL_MAP order
    const order = Object.keys(KEY_LABEL_MAP);
    return result.sort((a, b) => order.indexOf(a.key) - order.indexOf(b.key));
  })();

  // activeKey is the actual API result key for the currently selected tab from displayedTables
  const activeKey = displayedTables[activeTab]?.key || null;

  // If the set of filtered tables changes and the current activeTab is out-of-range,
  // clamp it back to a valid tab. This prevents an invisible UI when
  // the previous activeTab index no longer exists in the new filteredTables array.
  useEffect(() => {
    if (activeTab >= displayedTables.length) {
      // Default to FA (finalAnalysis) if the current tab is invalid
      const faIdx = displayedTables.findIndex(t => t.key === 'finalAnalysis');
      setActiveTab(faIdx >= 0 ? faIdx : 0);
    }
  }, [displayedTables.length]);

  useEffect(() => {
    setCurrentPage(1);
    // If FA tab, default to All (Infinity), else default to 10
    if (activeTab === 0) {
      setRecordsPerPage(Infinity);
    } else {
      setRecordsPerPage(RECORDS_PER_PAGE_OPTIONS[0]);
    }
  }, [activeTab]);

  const formatNumber = (value) => {
    if (typeof value === 'number') {
      return value.toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      });
    }
    return value;
  };

  const formatInteger = (value) => {
    if (typeof value === 'number') {
      return parseInt(value, 10).toLocaleString('en-US');
    }
    return value;
  };

  const formatDate = (value) => {
    if (!value) return '';
    const d = new Date(value);
    if (isNaN(d)) return value;
    return d.toISOString().slice(0, 10);
  };

  const renderTable = (tableData, tabKey) => {
    if (!Array.isArray(tableData) || tableData.length === 0) return <p>No data available</p>;

    // Use new redesigned components for key tabs
    if (tabKey === 'brokerAnalysis') {
      return <BrokerAnalysisTable tableData={tableData} highlightToggles={highlightToggles} />;
    }
    if (tabKey === 'topAccumulators') {
      return <TopAccumulatorsTableDesigned tableData={tableData} highlightToggles={highlightToggles} />;
    }
    if (tabKey === 'topDistributors') {
      return <TopDistributorsTableDesigned tableData={tableData} highlightToggles={highlightToggles} />;
    }

    // Header maps for different tabs
    const headerMapDefault = {
      'memberId': 'MemID',
      'brokerName': 'Broker',
      'scriptName': 'Script',
      'firstTradeDate': 'FirstDate',
      'lastTradeDate': 'LastDate',
      'buyQuantity': 'BuyQty',
      'maxBuyRate': 'MaxBuy',
      'avgBuyRate': 'AvgBuy',
      'sellQuantity': 'SellQty',
      'maxSellRate': 'MaxSell',
      'avgSellRate': 'AvgSell',
      'netQuantity': 'NetQty',
      'accumulation': 'Accum',
      'distribution': 'Distrib',
      'brokerActivity': 'BrokerAct',
      'accumulationRank': 'AccumRank',
      'distributionRank': 'DistribRank',
      'pctOfBuys': '% of Buys',
      'pctOfSells': '% of Sells',
      'averagePrice': 'Avg Price',
      'TopAccumulationTotal': 'Top Accumulation Total',
      'TopDistributionTotal': 'Top Distribution Total',
      'TopN_Accumulation-Distribution_Difference': 'Accumulation-Distribution Difference',
      'AvgBuyPriceTopN': 'Avg Buy Price (Top N)',
      'AvgSellPriceTopN': 'Avg Sell Price (Top N)',
      'MarketActivity': 'Market Activity',
      'TopNValue': 'Top N Value'
    };
    const headerMapMarketSummary = {
      'analysisStartDate': 'StartDate',
      'analysisEndDate': 'EndDate',
      'totalBuy': 'TotBuy',
      'totalSell': 'TotSell',
      'netMarketFlow': 'NetFlow',
      'totalAccumulation': 'TotAccum',
      'totalDistribution': 'TotDistrib',
      'pctAccumulation': 'PctAccum',
      'pctDistribution': 'PctDistrib',
      'scriptName': 'Script'
    };
    const headerMapFinalAnalysis = {
      'TopAccumulationTotal': 'TopAccum',
      'TopDistributionTotal': 'TopDistrib',
      'TopN_Accumulation-Distribution_Difference': 'AccumDistribDiff',
      'AvgBuyPriceTopN': 'AvgBuyTopN',
      'AvgSellPriceTopN': 'AvgSellTopN',
      'MarketActivity': 'MktActivity',
      'TopNValue': 'TopN_Value',
      'scriptName': 'Script'
    };
    const displayHeader = (header) => {
      if (tabKey === 'marketSummary') {
        return headerMapMarketSummary[header] || header;
      }
      if (tabKey === 'finalAnalysis') {
        return headerMapFinalAnalysis[header] || header;
      }
      return headerMapDefault[header] || header;
    };

    const totalItems = tableData.length;
    const totalPages = recordsPerPage === Infinity ? 1 : Math.ceil(totalItems / recordsPerPage);
    const displayData = recordsPerPage === Infinity ? tableData : tableData.slice((currentPage - 1) * recordsPerPage, currentPage * recordsPerPage);

    // Determine if current tab is a table where highlighting should apply
    const lowerKey = (tabKey || '').toLowerCase();
    const isTopAccTab = TABLE_LABELS[activeTab] === 'Top ACC' || (lowerKey.includes('top') && lowerKey.includes('acc'));
    const isBrkAnalysisTab = lowerKey.includes('brk') || TABLE_LABELS[activeTab] === 'Brk Analysis' || lowerKey.includes('brokeranalysis');
    const isTopDissTab = TABLE_LABELS[activeTab] === 'Top Diss' || (lowerKey.includes('top') && lowerKey.includes('diss'));
    const isHighlightTab = isTopAccTab || isBrkAnalysisTab || isTopDissTab;

    return (
      <>
        <div className="table-container">
          <table>
            <thead>
              <tr>
                <th>SN</th>
                {Object.keys(tableData[0] || {}).map((header) => (
                  <th key={header}>{displayHeader(header)}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {displayData.map((row, index) => {
                // Try to obtain broker/member id from various possible field names
                const rawId = (
                  row.brokerId ??
                  row.BrokerId ??
                  row.memberId ??
                  row.MemberId ??
                  row.brokerID ??
                  row.memberID
                );
                const brokerId = rawId != null ? parseInt(rawId, 10) : null;
                const highlightEnabledForTab = (
                  (isBrkAnalysisTab && highlightToggles.brk) ||
                  (isTopAccTab && highlightToggles.topAcc) ||
                  (isTopDissTab && highlightToggles.topDiss)
                );
                const shouldHighlight = isHighlightTab && highlightEnabledForTab && brokerId != null && HIGHLIGHT_BROKER_IDS.has(brokerId);
                return (
                <tr key={index} className={shouldHighlight ? 'highlight-broker' : undefined}>
                  <td>{recordsPerPage === Infinity ? index + 1 : (currentPage - 1) * recordsPerPage + index + 1}</td>
                  {Object.keys(row).map((header) => {
                    let cell = row[header];
                    if (
                      header === 'BrokerId' ||
                      header === 'brokerId' ||
                      header === 'memberId' ||
                      header === 'TopAccumulationTotal' ||
                      header === 'TopDistributionTotal' ||
                      header === 'TopN_Accumulation-Distribution_Difference' ||
                      header === 'TopNValue'
                    ) {
                      // Ensure these fields are shown as integer
                      cell = parseInt(cell, 10);
                    } else if (header.toLowerCase().includes('date')) {
                      cell = formatDate(cell);
                    } else if (typeof cell === 'number') {
                      cell = formatNumber(cell);
                    }
                    return <td key={header}>{cell}</td>;
                  })}
                </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {totalItems > RECORDS_PER_PAGE_OPTIONS[0] && (
          <div className="pagination">
            <button onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))} disabled={currentPage === 1 || recordsPerPage === Infinity}>
              Previous
            </button>
            {recordsPerPage !== Infinity ? (
              Array.from({ length: totalPages }, (_, i) => (
                <button
                  key={i + 1}
                  className={currentPage === i + 1 ? 'active' : ''}
                  onClick={() => setCurrentPage(i + 1)}
                >
                  {i + 1}
                </button>
              ))
            ) : (
              <span>Page 1 of 1</span>
            )}
            <button onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages))} disabled={currentPage === totalPages || recordsPerPage === Infinity}>
              Next
            </button>
            <select value={recordsPerPage} onChange={(e) => {
              setRecordsPerPage(Number(e.target.value));
              setCurrentPage(1);
            }}>
              {RECORDS_PER_PAGE_OPTIONS.map((option) => (
                <option key={option} value={option}>
                  {option === Infinity ? 'All' : option}
                </option>
              ))}
            </select>
          </div>
        )}
      </>
    );
  };

  const toYmd = (dateObj) => {
    if (!(dateObj instanceof Date) || Number.isNaN(dateObj.getTime())) return '';
    return dateObj.toISOString().slice(0, 10);
  };

  const isTradingDayDate = (dateObj) => {
    const day = dateObj.getDay();
    if (day === 0 || day === 6) return false;
    return !holidays[toYmd(dateObj)];
  };

  // Helper to get date range for a given number of trading days (skip weekend + known holidays)
  const getDateRange = (days, baseEndDate) => {
    let d = baseEndDate ? new Date(baseEndDate) : new Date();
    if (Number.isNaN(d.getTime())) d = new Date();

    // If end date not provided, before close we treat previous trading day as latest complete day.
    if (!baseEndDate) {
      const now = new Date();
      const sameDayTrading = isTradingDayDate(now);
      if (sameDayTrading && now.getHours() < 15) {
        d.setDate(d.getDate() - 1);
      }
    }

    // Always move to the most recent trading day
    while (!isTradingDayDate(d)) {
      d.setDate(d.getDate() - 1);
    }

    const end = d.toISOString().slice(0, 10);
    if (days === 1) {
      return { start: end, end };
    }
    let count = 0;
    // For >1d, go back until we have 'days' trading days.
    while (count < days - 1) {
      d.setDate(d.getDate() - 1);
      if (isTradingDayDate(d)) {
        count++;
      }
    }
    const start = d.toISOString().slice(0, 10);
    return { start, end };
  };

  // Helper to get the most recent trading day (never weekend/holiday)
  const getMostRecentTradingDay = (date) => {
    let d = new Date(date);
    while (!isTradingDayDate(d)) {
      d.setDate(d.getDate() - 1);
    }
    return d.toISOString().slice(0, 10);
  };

  // New helper to determine which days to show in the summary table
  const getSummaryDays = (activeRange) => {
    const allButtons = [1, 2, 3, 4, 5, 6, 7, 14, 30, 90, 120, 150, 180, 365, 1095, 2190];
    if (!activeRange) return [];
    if (activeRange <= 7) {
      return Array.from({ length: activeRange }, (_, i) => i + 1);
    }
    return allButtons.filter(d => d <= activeRange);
  };

  // Function to check if the server is available
  const checkServerStatus = async () => {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 5000); // 5 second timeout
      
      const response = await fetch(`${SERVER_API_BASE}/api/health`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
        signal: controller.signal
      });
      
      clearTimeout(timeoutId);
      
      if (!response.ok) {
        const errorData = await response.json();
        if (response.status === 503 && errorData.database === 'disconnected') {
          throw new Error('Database connection failed. The SQL Server might be unreachable.');
        }
        throw new Error(errorData.message || 'Server health check failed');
      }
      
      return true;
    } catch (error) {
      console.error('Server health check failed:', error);
      if (error.name === 'AbortError') {
        throw new Error('Server is not responding. Request timed out.');
      }
      throw error;
    }
  };

  // Extracted fetch logic for reuse
  const fetchData = async (formToUse, newActiveRange) => {
    // Cancel any existing request
    if (abortController) {
      abortController.abort();
    }

    // Create new abort controller for this request
    const newAbortController = new AbortController();
    setAbortController(newAbortController);

    setLoading(true);
    setError(null);
    setMarketData({});
    setAccumSummaryLoading(true);
    setAccumDistribDiffByRange({}); // Clear previous data

    try {
      // Check server status first
      try {
        await checkServerStatus();
      } catch (healthError) {
        // The checkServerStatus function now throws specific errors
        throw healthError;
      }
      
      // 1. Fetch main data for the selected range (e.g., 4d)
      const params = new URLSearchParams(formToUse).toString();
      console.log('Fetching data with params:', params);
      console.log('Form data being sent:', formToUse);
      const response = await fetch(`${SERVER_API_BASE}/api/market-data?${params}`, {
        signal: newAbortController.signal
      });
      
      if (!response.ok) {
        let errorMessage = 'Internal server error';
        try {
          const errorData = await response.json();
          errorMessage = errorData.error || errorData.details || response.statusText;
        } catch (e) {
          // If we can't parse the error as JSON, use the status text
          errorMessage = `Error: ${response.status} ${response.statusText}`;
        }
        throw new Error(`Failed to fetch market data: ${errorMessage}`);
      }
      
      const data = await response.json();
      console.log('Server response data:', data);
      console.log('Market Summary data:', data.marketSummary);
      console.log('Broker Analysis data:', data.brokerAnalysis);
      setMarketData(data);

      const currentDiff = (data.finalAnalysis && data.finalAnalysis[0])
        ? (data.finalAnalysis[0]['TopN_Accumulation-Distribution_Difference'] || data.finalAnalysis[0]['AccumDistribDiff'] || null)
        : null;

      // Initialize the summary data with the value for the selected range
      const newSummaryData = { [newActiveRange]: currentDiff };

      // 2. Fetch historical data for the summary table (e.g., 1d, 2d, 3d)
      if (newActiveRange > 1) {
        const summaryDaysToFetch = getSummaryDays(newActiveRange).filter(d => d < newActiveRange);
        
        const dateRanges = summaryDaysToFetch.map(days => {
          const { start, end } = getDateRange(days, formToUse.EndDate);
          return { days, start, end };
        });

        if (dateRanges.length > 0) {
          const multiParams = new URLSearchParams({
            ScriptName: formToUse.ScriptName || '',
            TopNRows: formToUse.TopNRows || 10,
            buyerMemberId: 0,
            dateRanges: JSON.stringify(dateRanges),
          }).toString();
          
          const multiResp = await fetch(`${SERVER_API_BASE}/api/market-data-multi?${multiParams}`, {
            signal: newAbortController.signal
          });
          
          if (multiResp.ok) {
            const historicalData = await multiResp.json();
            // Merge historical data with the current range's data
            Object.assign(newSummaryData, historicalData);
          } else {
            console.error('Failed to fetch historical summary data');
          }
        }
      }
      
      setAccumDistribDiffByRange(newSummaryData);

      // Set other states only on success
      setActiveTab(FA_TAB_INDEX);
      setCurrentPage(1);
      setRecordsPerPage(RECORDS_PER_PAGE_OPTIONS[0]);

    } catch (err) {
      console.error('Error fetching market data:', err);
      if (err.name === 'AbortError') {
        console.log('Request was cancelled');
        setError('Request cancelled');
      } else if (err.message.includes('Failed to fetch')) {
        // Network error - server might be down
        setError('Error: Failed to fetch market data. The server might be unreachable.');
      } else if (err.message.includes('Server is not responding')) {
        // Server health check failed
        setError('Server is not responding. Please try again later.');
      } else if (err.message.includes('database') || err.message.includes('SQL')) {
        // Database connection error
        setError('Database connection error. The SQL Server might be unreachable.');
      } else {
        setError(err.message);
      }
    } finally {
      setLoading(false);
      setAccumSummaryLoading(false);
      setAbortController(null);
    }
  };

  // Helper to get card color and title
  const getCardProps = (tabIdx) => {
    if (tabIdx === 0) return { className: 'card card-green', title: 'Broker Top Buy' };
    if (tabIdx === 1) return { className: 'card card-red', title: 'Broker Top Sell' };
    if (tabIdx === 2) return { className: 'card card-blue', title: 'Broker Top Holdings' };
    return { className: 'card', title: TABLE_LABELS[tabIdx] || '' };
  };

  // Holidays for Nepal stock market (2026)
  const holidays = {
    '2026-01-11': 'Prithvi Jayan',
    '2026-01-15': 'Maghe Sankranti',
    '2026-01-19': 'Sonam Lhosar',
    '2026-01-30': 'Martyrs Day',
    '2026-02-15': 'Mahashiva Ratri',
    '2026-02-18': 'Gyalpo Lhosar',
    '2026-02-19': 'Prajatantra Diwas',
    '2026-03-02': 'Fagu Purnima',
    '2026-03-04': 'Election Holiday',
    '2026-03-05': 'Election Holiday',
    '2026-03-06': 'Election Holiday',
    '2026-03-08': 'International Women Day',
    '2026-03-18': 'Ghode Jatra',
    '2026-03-27': 'Ram Nawami',
    '2026-04-14': 'New Year',
    '2026-05-01': 'Buddha Jayanti',
    '2026-05-29': 'Republic Day'
  };

  // Helper to format date to YYYY-MM-DD string
  const formatDateToString = (dateObj) => {
    if (!dateObj) return '';
    if (!(dateObj instanceof Date)) dateObj = new Date(dateObj);
    if (isNaN(dateObj)) return '';
    return dateObj.toISOString().slice(0, 10);
  };

  // Helper to check if a specific date is a holiday
  const isHoliday = (dateObj) => {
    const dateStr = formatDateToString(dateObj);
    return !!holidays[dateStr];
  };

  // Helper to get holiday name for a specific date
  const getHolidayName = (dateObj) => {
    const dateStr = formatDateToString(dateObj);
    return holidays[dateStr] || null;
  };

  // Helper to check if today is a non-trading day (Saturday, Sunday, or Holiday)
  const isNonTradingDay = () => {
    const today = new Date();
    const day = today.getDay();
    const isWeekend = day === 6 || day === 0; // Saturday (6) or Sunday (0)
    const isHol = isHoliday(today);
    return isWeekend || isHol;
  };

  // Helper to get non-trading day reason
  const getNonTradingReason = () => {
    const today = new Date();
    const day = today.getDay();
    const holidayName = getHolidayName(today);
    
    if (holidayName) {
      return `The market is closed today (${holidayName}).`;
    }
    if (day === 6) {
      return 'The market is closed today (Saturday).';
    }
    if (day === 0) {
      return 'The market is closed today (Sunday).';
    }
    return 'The market is closed today.';
  };

  // Header summary counts from Brk Analysis rows.
  const brkAnalysisTable = filteredTables.find(({ key }) => key.toLowerCase() === 'brokeranalysis')?.table
    || filteredTables.find(({ key }) => key.toLowerCase().includes('brk'))?.table
    || [];
  const visibleRows = brkAnalysisTable.filter((row) => {
    const name = (row.brokerName || row.BrokerName || '').toString().toUpperCase();
    return name && name !== 'TOTAL';
  });
  const getBrokerAct = (row) => (
    row.brokerActivity
    || row.BrokerAct
    || row['Broker Activity']
    || row.broker_activity
    || ''
  ).toString().toLowerCase();
  const totalBrokers = visibleRows.length;
  const totalAccumulators = visibleRows.filter((row) => getBrokerAct(row).includes('accum')).length;
  const totalDistributors = visibleRows.filter((row) => getBrokerAct(row).includes('distribut')).length;

  return (
    <div className={`App${theme ? ` theme-${theme}` : ''}`}>
      {isNonTradingDay() && !isNoticeDismissed && (
        <div className="notice-banner" role="status" aria-live="polite">
          <span>
            {getNonTradingReason()} Latest data shown is from the most recent trading day.
          </span>
          <button
            type="button"
            className="notice-banner-close"
            onClick={() => setIsNoticeDismissed(true)}
            aria-label="Close market notice"
            title="Close"
          >
            ×
          </button>
        </div>
      )}
      <header className="App-header">
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          {/* Left: Script Name input */}
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <label className="header-script-label" style={{ display: 'flex', flexDirection: 'column', minWidth: 120, marginBottom: 0 }}>
              Script Name:
              <div className="header-script-pill">
                <input
                  className="header-script-input header-script-input--in-pill"
                  type="text"
                  name="ScriptName"
                  value={form.ScriptName}
                  onChange={handleChange}
                  style={{ width: 100 }}
                  autoComplete="off"
                  spellCheck={false}
                />
                {(form.ScriptName || '').trim() ? (
                  <span
                    className="header-script-ltp-inline"
                    title="Last Traded Price (LTP)"
                  >
                    {headerLtpLoading
                      ? '(…)'
                      : headerLtp != null
                        ? `(${headerLtp.toFixed(2)})`
                        : '(—)'}
                  </span>
                ) : null}
              </div>
            </label>
          </div>
          {/* Center: Title and script name heading */}
          <div style={{ flex: 1, textAlign: 'center' }}>
            <h1 style={{ margin: 0 }}>BLACK HORSE DASHBOARD</h1>
            <div className="nepse-widget nepse-widget--single-line">
              <span className="nepse-widget__title">NEPSE Index</span>
              <span className="nepse-widget__menu" aria-hidden="true">≡</span>
              <span className="nepse-widget__value">
                {Number.isFinite(nepseIndex)
                  ? nepseIndex.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                  : '--'}
              </span>
              <span className={`nepse-widget__change ${(nepseIndexMeta.change ?? 0) < 0 ? 'is-negative' : 'is-positive'}`}>
                <span className="nepse-widget__arrow" aria-hidden="true">
                  {(nepseIndexMeta.change ?? 0) < 0 ? '⌄' : '^'}
                </span>
                <span>
                  {nepseIndexMeta.change != null ? Math.abs(nepseIndexMeta.change).toFixed(2) : '--'}
                </span>
                <span>
                  {nepseIndexMeta.perChange != null ? `${Math.abs(nepseIndexMeta.perChange).toFixed(2)}%` : '--'}
                </span>
              </span>
              <span
                className={`nepse-widget__status ${
                  nepseIndexMeta.marketStatus === 'MARKET OPEN'
                    ? 'is-open'
                    : nepseIndexMeta.marketStatus === 'MARKET PRE-OPEN'
                      ? 'is-preopen'
                      : 'is-closed'
                }`}
              >
                {nepseIndexMeta.marketStatus || 'MARKET CLOSED'}
              </span>
            </div>
            {form.ScriptName && (
              <div className="script-name-heading script-name-header" style={{ marginTop: 3 }}>
                Script Name: {SCRIPT_NAME_MAP[form.ScriptName] || form.ScriptName}
                {' | '}
                Brokers: {loading ? '-' : totalBrokers}
                {' | '}
                Accumulators: {loading ? '-' : totalAccumulators}
                {' | '}
                Distributors: {loading ? '-' : totalDistributors}
              </div>
            )}
          </div>
          {/* Right: Theme dropdown and highlight toggles */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', minWidth: 180 }}>
            <select
              className="dropdown"
              value={theme}
              onChange={e => setTheme(e.target.value)}
            >
              {THEMES.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
            <div style={{ marginLeft: 12 }}>
              <details>
                <summary style={{ cursor: 'pointer', userSelect: 'none' }}>Highlight</summary>
                <div style={{ background: '#2c2f33', padding: '8px 12px', borderRadius: 6, marginTop: 6 }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                    <input type="checkbox" checked={highlightToggles.brk} onChange={e => setHighlightToggles(s => ({ ...s, brk: e.target.checked }))} />
                    Brk Analysis
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                    <input type="checkbox" checked={highlightToggles.topAcc} onChange={e => setHighlightToggles(s => ({ ...s, topAcc: e.target.checked }))} />
                    Top ACC
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <input type="checkbox" checked={highlightToggles.topDiss} onChange={e => setHighlightToggles(s => ({ ...s, topDiss: e.target.checked }))} />
                    Top Diss
                  </label>
                </div>
              </details>
            </div>
          </div>
        </div>
      </header>

      <main className="market-data">
        <div className="market-data__main">
        <div className="control-bar">
          <div className="control-bar__left">
            <div className="range-btn-group">
              <div className="range-btn-group__inner" aria-label="Range selector">
                {[1, 2, 3, 4, 5, 6, 7, 14, 30, 90, 120, 150, 180, 365, 1095, 2190].map((d) => {
                  const label = d === 30 ? '1M' : d === 90 ? '3M' : d === 120 ? '4M' : d === 150 ? '5M' : d === 180 ? '6M' : d === 365 ? '1Y' : d === 1095 ? '3Y' : d === 2190 ? '6Y' : `${d}d`;
                  return (
                    <button
                      key={d}
                      type="button"
                      className={activeRange === d ? 'range-btn active' : 'range-btn'}
                      onClick={() => {
                        const { start, end } = getDateRange(d);
                        const newForm = { ...form, StartDate: start, EndDate: end };
                        setForm(newForm);
                        setActiveRange(d);
                        fetchData(newForm, d);
                      }}
                    >
                      {label}
                    </button>
                  );
                })}
                <button
                  type="button"
                  className={`top-brokers-toggle${topBrokersOpen ? ' top-brokers-toggle--active' : ''}`}
                  onClick={() => setTopBrokersOpen(prev => !prev)}
                  style={{ marginLeft: 8 }}
                >
                  TBA
                </button>
                <button
                  type="button"
                  className="toggle-filters-icon"
                  onClick={() => setShowFilters((prev) => !prev)}
                  style={{ cursor: 'pointer', width: 32, height: 32, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--theme-accent, #3949ab)', borderRadius: '999px', border: '1px solid transparent', background: 'transparent', marginLeft: 4 }}
                  title={showFilters ? 'Hide Filters' : 'Show Filters'}
                  aria-label={showFilters ? 'Hide filters' : 'Show filters'}
                >
                  {showFilters ? (
                    // Eye-off SVG
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17.94 17.94A10.06 10.06 0 0 1 12 20c-5 0-9.27-3.11-11-8 1.21-3.06 3.6-5.5 6.58-6.71M1 1l22 22" /><path d="M9.53 9.53A3.5 3.5 0 0 0 12 16a3.5 3.5 0 0 0 2.47-6.47" /></svg>
                  ) : (
                    // Eye SVG
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="3.5" /><path d="M2 12C3.73 7.11 7.99 4 12 4s8.27 3.11 10 8c-1.73 4.89-5.99 8-10 8s-8.27-3.11-10-8z" /></svg>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
        {showFilters && (
          <form className="market-form" onSubmit={handleSubmit}>
            <label>
              Script Name:
              <input type="text" name="ScriptName" value={form.ScriptName} onChange={handleChange} style={{ width: 120 }} />
            </label>
            <label>
              Start Date:
              <input type="date" name="StartDate" value={form.StartDate} onChange={handleChange} required style={{ width: 120 }} />
            </label>
            <label>
              End Date:
              <input type="date" name="EndDate" value={form.EndDate} onChange={handleChange} required style={{ width: 120 }} />
            </label>
            <label>
              Byer ID:
              <input type="number" name="buyerMemberId" value={form.buyerMemberId} onChange={handleChange} min="0" style={{ width: 90 }} />
            </label>
            <label>
              Top N Rows:
              <input type="number" name="TopNRows" value={form.TopNRows} onChange={handleChange} min="1" style={{ width: 90 }} />
            </label>
            <button type="submit" disabled={loading}>Fetch Data</button>
          </form>
        )}
        {loading && <ThemedLoader theme={theme} />}
        {error && (
          <div className="error-message">{error}</div>
        )}
        {!loading && !error && Object.keys(marketData).length === 0 && (
          <div className="empty-state">
            <p>No meaningful data available</p>
            <p>Try selecting a different time range or script</p>
          </div>
        )}
        {filteredTables.length > 0 && (
          <div className="tabs-container">
            <div className="tabs">
              {displayedTables.map(({ key }, tabIdx) => (
                <button
                  key={key}
                  className={activeTab === tabIdx ? 'tab active' : 'tab'}
                  onClick={() => { setActiveTab(tabIdx); }}
                >
                  {KEY_LABEL_MAP[key] || TABLE_LABELS[tabIdx] || key}
                </button>
              ))}
            </div>
            <div className="data-grid">
              {activeKey === 'algoEngine' ? (
                <AlgoEngineSection 
                  scriptName={form.ScriptName} 
                  activeTab={activeTab}
                  displayedTables={displayedTables}
                  activeKey={activeKey}
                  startDate={form.StartDate}
                  endDate={form.EndDate}
                />
              ) : ['accumulationDistribution'].includes(activeKey) ? (
                <AccDisTable 
                  marketData={marketData} 
                  theme={theme}
                  topAccumulatorsData={filteredTables.find(({ key }) => key === 'topAccumulators')?.table || []}
                  topDistributorsData={filteredTables.find(({ key }) => key === 'topDistributors')?.table || []}
                  highlightToggles={highlightToggles}
                />
              ) : (
                // For non-Acc/Dis tabs, render using the matching filteredTables entry (preserve original behavior)
                renderTable(filteredTables.find(({ key }) => key === activeKey)?.table || [], activeKey)
              )}
              {/* --- AccumDistribDiff summary below FA tab table --- */}
              {activeKey === 'finalAnalysis' && (
                <div className={`accum-distrib-summary theme-${theme || 'minimalist-dark'}`}>
                  <table className="accum-distrib-summary__table">
                    <thead>
                      <tr>
                        <th className="accum-distrib-summary__label">Days</th>
                        {(() => {
                          if (!activeRange) return null;
                          const shown = getSummaryDays(activeRange);
                          return shown.map((d) => (
                            <th
                              key={d}
                              className="accum-distrib-summary__day"
                            >
                              {d === 30 ? '1M' : d === 90 ? '3M' : d === 120 ? '4M' : d === 150 ? '5M' : d === 180 ? '6M' : d === 365 ? '1Y' : d === 1095 ? '3Y' : d === 2190 ? '6Y' : `${d}d`}
                            </th>
                          ));
                        })()}
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td className="accum-distrib-summary__label">ACC/Dis</td>
                        {accumSummaryLoading ? (
                          <td
                            colSpan={activeRange ? getSummaryDays(activeRange).length : 1}
                            className="accum-distrib-summary__loading"
                          >
                            Loading...
                          </td>
                        ) : (
                          (() => {
                            if (!activeRange) return null;
                            const shown = getSummaryDays(activeRange);
                            return shown.map((d) => {
                              const curr = accumDistribDiffByRange[d];
                              return (
                                <td
                                  key={d}
                                  className={`accum-distrib-summary__cell${d === activeRange ? ' accum-distrib-summary__cell--active' : ''}`}
                                >
                                  {typeof curr === 'number' ? formatInteger(curr) : '-'}
                                </td>
                              );
                            });
                          })()
                        )}
                      </tr>
                      <tr>
                        <td className="accum-distrib-summary__label accum-distrib-summary__label--strong">Total</td>
                        <td
                          colSpan={activeRange ? getSummaryDays(activeRange).length : 1}
                          className="accum-distrib-summary__total"
                        >
                          {accumDistribDiffByRange[activeRange] !== undefined && accumDistribDiffByRange[activeRange] !== null
                            ? formatInteger(accumDistribDiffByRange[activeRange])
                            : 0}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}
        {filteredTables.length === 0 && !loading && <p>No meaningful data available</p>}
        </div>
          <aside className={`market-data__sidebar ${topBrokersOpen ? 'sidebar--open' : 'sidebar--collapsed'}`}>
            <TopBrokers
              theme={theme}
              topAccumulatorsData={filteredTables.find(({ key }) => key === 'topAccumulators')?.table || []}
              isOpen={topBrokersOpen}
              onToggle={() => setTopBrokersOpen(prev => !prev)}
            />
          </aside>
        </main>
    </div>
  );
}

export default App;
